import { useState, useCallback } from 'react';
import { ContactFormData, ContactFormErrors, ContactFormState } from '@/components/contact/ContactForm.types';

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  subject: '',
  description: ''
};

const initialErrors: ContactFormErrors = {};

export const useContactForm = () => {
  const [state, setState] = useState<ContactFormState>({
    data: initialFormData,
    errors: initialErrors,
    isSubmitting: false,
    isSuccess: false,
    isError: false
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (field: keyof ContactFormData, value: string): string | undefined => {
    if (!value.trim()) {
      return `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }

    switch (field) {
      case 'email':
        if (!validateEmail(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'subject':
        if (value.length > 60) {
          return 'Subject must be 60 characters or less';
        }
        break;
      case 'description':
        if (value.length > 2000) {
          return 'Description must be 2000 characters or less';
        }
        break;
    }

    return undefined;
  };

  const validateForm = (data: ContactFormData): ContactFormErrors => {
    const errors: ContactFormErrors = {};
    
    Object.keys(data).forEach((key) => {
      const field = key as keyof ContactFormData;
      const error = validateField(field, data[field]);
      if (error) {
        errors[field] = error;
      }
    });

    return errors;
  };

  const updateField = useCallback((field: keyof ContactFormData, value: string) => {
    setState(prev => {
      const newData = { ...prev.data, [field]: value };
      const newErrors = { ...prev.errors };
      
      // Clear error when user starts typing
      if (newErrors[field]) {
        delete newErrors[field];
      }

      // Validate field in real-time
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }

      return {
        ...prev,
        data: newData,
        errors: newErrors
      };
    });
  }, []);

  const isFormValid = useCallback((): boolean => {
    const errors = validateForm(state.data);
    return Object.keys(errors).length === 0 && 
           Object.values(state.data).every(value => value.trim() !== '');
  }, [state.data]);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setState(prev => ({ ...prev, isSubmitting }));
  }, []);

  const setSuccess = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isSuccess: true, 
      isSubmitting: false,
      isError: false 
    }));
  }, []);

  const setError = useCallback((errorMessage: string) => {
    setState(prev => ({ 
      ...prev, 
      isError: true, 
      isSubmitting: false,
      errorMessage 
    }));
  }, []);

  const resetForm = useCallback(() => {
    setState({
      data: initialFormData,
      errors: initialErrors,
      isSubmitting: false,
      isSuccess: false,
      isError: false
    });
  }, []);

  return {
    state,
    updateField,
    isFormValid,
    setSubmitting,
    setSuccess,
    setError,
    resetForm
  };
};

